import {useScrollPosition} from '@n8tb1t/use-scroll-position'
import {tokens} from '@pleo-io/telescope'
import {Disclosure, DisclosurePanel} from '@reach/disclosure'
import {motion, AnimatePresence} from 'framer-motion'
import {graphql, StaticQuery} from 'gatsby'
import Cookies from 'js-cookie'
import React, {useState, ReactElement} from 'react'

import {HeaderQuery} from '@/graphql-types'
import useHasMounted from '@/hooks/use-has-mounted'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {durationStringToNumber} from '@/utils/duration-string-to-number'
import {isValidBackgroundColor} from '@/utils/is-valid-background-color'
import ExternalLink from '@helios/shared/components/external-link'
import Image from '@helios/shared/components/image'
import LocalizedLink from '@helios/shared/components/localized-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import RichText from '@helios/shared/components/rich-text'
import SmoothScrollLink from '@helios/shared/components/smooth-scroll-link'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import logoAngled from '@helios/shared/images/icons/logo-angled.svg'
import logoHorizontal from '@helios/shared/images/icons/logo-horizontal.svg'
import menuCloseIcon from '@helios/shared/images/icons/menu-close.svg'
import menuOpenIcon from '@helios/shared/images/icons/menu-open.svg'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {usePageInfo} from '@helios/shared/providers/page-info'
import {Text} from '@helios/shared/styles/typography'
import {breakpoints} from '@helios/shared/theme'
import {PrismicData} from '@helios/shared/types/prismic-data'
import {getExperiments} from '@helios/shared/utils/get-experiments'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'

import * as s from './header.styles'

interface Props {
    hideNav?: boolean
    ctaDestination?: 'signupform' | 'demopage'
}

type Content = PrismicData<HeaderQuery, 'allPrismicHeader'>['data']

interface NavDropdownProps {
    id: string
    title: Content['product_dropdown_title']
    body: Content['product_dropdown_body']
    links: Content['product_dropdown_links']
    content:
        | ReactElement<Content['product_dropdown_content']>
        | ReactElement<Content['why_pleo_dropdown_content_team']>
        | ReactElement<Content['why_pleo_dropdown_content_industry']>
        | ReactElement<Content['resources_dropdown_content']>
    betaLabel?: string
}

interface WhyPleoDropdownContentColumnProps {
    linkItems:
        | Content['why_pleo_dropdown_content_industry']
        | Content['why_pleo_dropdown_content_team']
    categoryTitle: string
    categoryShortSentence: string
}

const ProductDropdownContentArea: React.FC<{
    content: Content['product_dropdown_content']
    betaLabel: string
}> = ({content, betaLabel}) => {
    if (!content) {
        return null
    }

    return (
        <s.DropdownContentArea columns={2}>
            {content.map((item) => {
                const iconBgColor = isValidBackgroundColor(item.icon_background_color)
                    ? item.icon_background_color
                    : 'default'

                return (
                    <s.DropdownContentAreaItem key={item.title} as={PrismicLink} {...item.link}>
                        {!!item.icon && (
                            <s.IconWrapper color={iconBgColor}>
                                <Image image={item.icon} width={80} />
                            </s.IconWrapper>
                        )}

                        <div>
                            <Text size="sm" font="primary">
                                {item.title} {item.is_beta && <s.BetaTag>{betaLabel}</s.BetaTag>}
                            </Text>

                            <s.DropdownItemBodyTextWrapper>
                                <RichText
                                    size="xxs"
                                    font="secondary"
                                    color="muted"
                                    content={item.body.raw}
                                />
                            </s.DropdownItemBodyTextWrapper>
                        </div>
                    </s.DropdownContentAreaItem>
                )
            })}
        </s.DropdownContentArea>
    )
}

const WhyPleoDropdownContentColumn: React.FC<WhyPleoDropdownContentColumnProps> = ({
    linkItems,
    categoryTitle,
    categoryShortSentence,
}) => {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <s.WhyPleoDropdownContentColumn $isExpanded={isOpen}>
            <s.WhyPleoDropdownHeadingWrapper>
                <s.WhyPleoDropdownCategoryTitle onClick={() => setIsOpen(!isOpen)}>
                    {categoryTitle}
                    <s.Arrow $isExpanded={isOpen} aria-hidden="true" />
                </s.WhyPleoDropdownCategoryTitle>
                <s.WhyPleoDropdownCategorySentence>
                    {categoryShortSentence}
                </s.WhyPleoDropdownCategorySentence>
            </s.WhyPleoDropdownHeadingWrapper>

            <s.NestedDropdown $isExpanded={isOpen}>
                {linkItems?.map((item, i) => {
                    return (
                        <s.DropdownContentAreaItem
                            key={`${item.title}-${i}`}
                            as={PrismicLink}
                            {...item.link}
                        >
                            {!!item.icon && <Image image={item.icon} width={40} />}

                            <Text size="sm" font="primary">
                                {item.title}
                            </Text>
                        </s.DropdownContentAreaItem>
                    )
                })}
            </s.NestedDropdown>
        </s.WhyPleoDropdownContentColumn>
    )
}

const ResourcesDropdownContentArea: React.FC<{
    content: Content['resources_dropdown_content']
}> = ({content}) => {
    if (!content) {
        return null
    }

    return (
        <s.DropdownContentArea columns={3}>
            {content.map((item, i) => {
                return (
                    <s.DropdownContentAreaItem
                        key={`${item.title}-${i}`}
                        as={PrismicLink}
                        {...item.link}
                    >
                        {!!item.icon && <Image image={item.icon} width={40} />}

                        <div>
                            <Text size="sm" font="primary">
                                {item.title}
                            </Text>

                            <s.DropdownItemBodyTextWrapper>
                                <RichText
                                    size="xxs"
                                    font="secondary"
                                    color="muted"
                                    content={item.body.raw}
                                />
                            </s.DropdownItemBodyTextWrapper>
                        </div>
                    </s.DropdownContentAreaItem>
                )
            })}
        </s.DropdownContentArea>
    )
}

const NavDropdown: React.FC<NavDropdownProps> = ({id, title, body, links, content}) => {
    return (
        <s.DropdownWrapper>
            <s.Dropdown>
                <s.DropdownInner>
                    <s.DropdownTitleArea>
                        <s.DropdownTitleAreaHeader>
                            <RichText size="lg" font="primary" content={title.raw} />
                            <RichText
                                size="xs"
                                font="secondary"
                                color="muted"
                                content={body.raw}
                                id={`header-${id}-description`}
                            />
                        </s.DropdownTitleAreaHeader>

                        {hasNonEmptyItems(links) && (
                            <s.DropdownTitleAreaLinks>
                                {links.map((linkItem, i) => {
                                    return (
                                        <s.DropdownTitleAreaLink
                                            key={`${linkItem.link_title}-${i}`}
                                            as={PrismicLink}
                                            {...linkItem.link}
                                        >
                                            {linkItem.link_title}{' '}
                                            <s.LinkArrow aria-hidden="true">→</s.LinkArrow>
                                        </s.DropdownTitleAreaLink>
                                    )
                                })}
                            </s.DropdownTitleAreaLinks>
                        )}
                    </s.DropdownTitleArea>
                    {content}
                </s.DropdownInner>
            </s.Dropdown>
        </s.DropdownWrapper>
    )
}

interface HeaderProps extends Props {
    content: Content
}

// This is the name of the cookie set in the web app which stores the current session status of a user.
const SESSION_STATUS_KEY = 'isLoggedIn'

export const Header: React.FC<HeaderProps> = ({
    content,
    hideNav,
    ctaDestination = 'signupform',
}) => {
    const [selectedDropdownIndex, setSelectedDropdownIndex] = useState<number | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [isThresholdReached, setIsThresholdReached] = useState(false)
    const isSmallScreen = useMediaQuery.max(s.mobileMenuBreakpoint)
    const experiments = getExperiments()
    const {pageContext} = usePageInfo()
    const {home, menu} = useGlobalStrings()
    const showStickyCta = isThresholdReached && !menuOpen && isSmallScreen
    const [isLoggedIn, setIsLoggedIn] = React.useState(false)
    const hasMounted = useHasMounted()

    React.useEffect(() => {
        setIsLoggedIn(Cookies.get(SESSION_STATUS_KEY) === 'true')
    }, [])

    useScrollPosition(({currPos}) => {
        setIsThresholdReached(currPos.y < -330)
    })

    const toggleMenu = () => {
        setSelectedDropdownIndex(null)
        setMenuOpen((isOpen) => !isOpen)
    }

    const handleTouchOrClick = (dropdownIndex: number) => {
        setSelectedDropdownIndex(selectedDropdownIndex !== dropdownIndex ? dropdownIndex : null)
    }

    const handleHover = (dropdownIndex: number | null) => {
        if (window.innerWidth >= breakpoints[s.tabletMenuBreakpoint]) {
            setSelectedDropdownIndex(dropdownIndex)
        }
    }

    const sectionsWithDropdown = [
        {
            id: 'product',
            label: content.product_button_label,
            title: content.product_dropdown_title,
            body: content.product_dropdown_body,
            links: content.product_dropdown_links,
            isBeta: content.is_beta_label,
            content: (
                <ProductDropdownContentArea
                    content={content.product_dropdown_content}
                    betaLabel={content.is_beta_label}
                />
            ),
        },
        {
            id: 'why-pleo',
            label: content.why_pleo_button_label,
            title: content.why_pleo_dropdown_title,
            body: content.why_pleo_dropdown_body,
            links: content.why_pleo_dropdown_links,
            content: (
                <s.DropdownContentArea columns={2}>
                    <WhyPleoDropdownContentColumn
                        categoryTitle={content.why_pleo_dropdown_industry_label}
                        categoryShortSentence={content.why_pleo_dropdown_industry_short_sentence}
                        linkItems={content.why_pleo_dropdown_content_industry}
                    />
                    <WhyPleoDropdownContentColumn
                        categoryTitle={content.why_pleo_dropdown_team_label}
                        categoryShortSentence={content.why_pleo_dropdown_team_short_sentence}
                        linkItems={content.why_pleo_dropdown_content_team}
                    />
                </s.DropdownContentArea>
            ),
        },
        {
            id: 'resources',
            label: content.resources_button_label,
            title: content.resources_dropdown_title,
            body: content.resources_dropdown_body,
            links: content.resources_dropdown_links,
            content: <ResourcesDropdownContentArea content={content.resources_dropdown_content} />,
        },
    ]

    const transition = {
        type: 'tween',
        duration: durationStringToNumber(tokens.smooth) / 1000,
    }

    const animationOverlayProps = {
        initial: {opacity: 0},
        animate: {opacity: 1},
        exit: {opacity: 0},
        transition,
    }

    const variants = {
        active: {
            opacity: 1,
            height: 'auto',
            transition,
            display: 'block',
        },
        inactive: {
            opacity: 0,
            height: 0,
            transition,
            transitionEnd: {
                display: 'none',
            },
        },
    }

    const getCtaButton = (ctaDestination: Props['ctaDestination']) => {
        switch (ctaDestination) {
            case 'signupform': {
                return (
                    <s.NavCta
                        as={SmoothScrollLink}
                        selector="#signupform"
                        onClick={() => {
                            setMenuOpen(false)
                            setSelectedDropdownIndex(null)
                        }}
                    >
                        {content.start_free_trial}
                    </s.NavCta>
                )
            }

            case 'demopage': {
                return (
                    <s.NavCta as={LocalizedLink} to="/demo">
                        {content.start_free_trial}
                    </s.NavCta>
                )
            }

            default: {
                throw new Error('Unrecognized destination')
            }
        }
    }

    return (
        <>
            <AnimatePresence>
                {(selectedDropdownIndex !== null || menuOpen) && (
                    <s.Overlay
                        {...animationOverlayProps}
                        mobileMenuOpen={menuOpen}
                        dropdownOpen={selectedDropdownIndex !== null}
                    />
                )}
            </AnimatePresence>

            <s.HeaderWrapper $menuOpen={menuOpen} enabled={menuOpen} forwardedAs="header">
                <s.HeaderInner $menuOpen={menuOpen}>
                    <s.DesktopLogo>
                        <LocalizedLink to="/">
                            <img src={logoAngled} alt={home} />
                        </LocalizedLink>
                    </s.DesktopLogo>

                    {showStickyCta ? (
                        <s.StickyCtaWrapper>{getCtaButton(ctaDestination)}</s.StickyCtaWrapper>
                    ) : (
                        <s.MobileLogo>
                            <LocalizedLink to="/">
                                <img src={logoHorizontal} alt={home} />
                            </LocalizedLink>
                        </s.MobileLogo>
                    )}

                    {!hideNav && (
                        <>
                            <s.Nav>
                                <s.NavGroupLeft>
                                    {sectionsWithDropdown.map((section, i) => {
                                        const isActive = selectedDropdownIndex === i

                                        return (
                                            <s.DisclosureWrapper
                                                onMouseEnter={() => handleHover(i)}
                                                onMouseLeave={() => handleHover(null)}
                                                key={section.id}
                                            >
                                                <Disclosure open={isActive} id={section.id}>
                                                    <s.MenuBtnWithDropdown
                                                        onClick={() => handleTouchOrClick(i)}
                                                    >
                                                        <s.NavLink
                                                            as="span"
                                                            $isHighlighted={isActive}
                                                        >
                                                            {section.label}
                                                            <s.Arrow
                                                                $isExpanded={isActive}
                                                                aria-hidden="true"
                                                            />
                                                        </s.NavLink>
                                                    </s.MenuBtnWithDropdown>

                                                    <DisclosurePanel hidden={false}>
                                                        <motion.div
                                                            variants={variants}
                                                            initial={false}
                                                            key={`${section.label}-${i}`}
                                                            animate={
                                                                isActive ? 'active' : 'inactive'
                                                            }
                                                        >
                                                            <NavDropdown {...section} />
                                                        </motion.div>
                                                    </DisclosurePanel>
                                                </Disclosure>
                                            </s.DisclosureWrapper>
                                        )
                                    })}

                                    <s.NavLink as={LocalizedLink} to="/pricing">
                                        {content.pricing}

                                        {content.show_pricing_badge && (
                                            <s.MenuBadge aria-hidden="true" />
                                        )}
                                    </s.NavLink>
                                </s.NavGroupLeft>

                                <s.NavGroupRight $menuOpen={menuOpen}>
                                    {!hasMounted ? null : isLoggedIn ? (
                                        <s.CtaWrapper $menuOpen={menuOpen}>
                                            <s.NavCta
                                                as={ExternalLink}
                                                to={process.env.GATSBY_PLEO_APP_URL}
                                            >
                                                {content.app_link_text}
                                            </s.NavCta>
                                        </s.CtaWrapper>
                                    ) : (
                                        <>
                                            <s.NavLink
                                                as={ExternalLink}
                                                to={`${process.env.GATSBY_PLEO_APP_URL}/login`}
                                            >
                                                {content.log_in}
                                            </s.NavLink>

                                            {!pageContext.hideDemoButton &&
                                                experiments.bookADemoButton === 'control' && (
                                                    <s.NavLink as={LocalizedLink} to="/demo">
                                                        {content.book_a_demo}
                                                    </s.NavLink>
                                                )}

                                            <s.CtaWrapper $menuOpen={menuOpen}>
                                                {getCtaButton(ctaDestination)}
                                            </s.CtaWrapper>
                                        </>
                                    )}
                                </s.NavGroupRight>
                            </s.Nav>

                            <s.MobileMenuBtn onClick={toggleMenu}>
                                <img src={menuOpen ? menuCloseIcon : menuOpenIcon} alt={menu} />
                            </s.MobileMenuBtn>
                        </>
                    )}
                </s.HeaderInner>
            </s.HeaderWrapper>
        </>
    )
}

const HeaderContainer: React.FC<Props & HeaderQuery> = ({allPrismicHeader, ...props}) => {
    const [content] = useLocalizedContent(allPrismicHeader.nodes)
    return <Header content={content.data} {...props} />
}

const query = graphql`
    query Header {
        allPrismicHeader {
            nodes {
                lang
                data {
                    pricing
                    show_pricing_badge
                    partners
                    start_free_trial
                    book_a_demo
                    log_in
                    app_link_text
                    is_beta_label
                    product_button_label
                    product_dropdown_title {
                        raw
                    }
                    product_dropdown_body {
                        raw
                    }
                    product_dropdown_links {
                        link_title
                        link {
                            ...PrismicLink
                        }
                    }
                    product_dropdown_content {
                        icon {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body {
                            raw
                        }
                        icon_background_color
                        is_beta
                        link {
                            ...PrismicLink
                        }
                    }
                    why_pleo_button_label
                    why_pleo_dropdown_title {
                        raw
                    }
                    why_pleo_dropdown_body {
                        raw
                    }
                    why_pleo_dropdown_links {
                        link_title
                        link {
                            ...PrismicLink
                        }
                    }
                    why_pleo_dropdown_team_label
                    why_pleo_dropdown_team_short_sentence
                    why_pleo_dropdown_content_team {
                        icon {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        link {
                            ...PrismicLink
                        }
                    }
                    why_pleo_dropdown_industry_label
                    why_pleo_dropdown_industry_short_sentence
                    why_pleo_dropdown_content_industry {
                        icon {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        link {
                            ...PrismicLink
                        }
                    }
                    resources_button_label
                    resources_dropdown_title {
                        raw
                    }
                    resources_dropdown_body {
                        raw
                    }
                    resources_dropdown_links {
                        link_title
                        link {
                            ...PrismicLink
                        }
                    }
                    resources_dropdown_content {
                        icon {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body {
                            raw
                        }
                        link {
                            ...PrismicLink
                        }
                    }
                }
            }
        }
    }
`

const HeaderQueryContainer: React.FC<Props> = (props) => (
    <StaticQuery
        query={`${query}`}
        render={(data: HeaderQuery) => <HeaderContainer {...props} {...data} />}
    />
)

export default HeaderQueryContainer
